<template>
  <div class="bs-page">
    <div class="bs-grid-overlay" aria-hidden="true"></div>
    <div class="bs-glow bs-glow-one" aria-hidden="true"></div>

    <StandardNavBar />

    <main class="content-page">
      <section class="content-shell">
      <p class="eyebrow">
        <span class="eyebrow-line"></span>
        Client feedback
      </p>

      <h1>
        What clients say
        <span>about working with us.</span>
      </h1>

      <p class="lead">
        Blue Speed supports building owners, MCSTs, managing agents, facility teams and main contractors
        across maintenance, repair and rope access works. Here is the feedback we hear most often.
      </p>

      <!--
        TODO (before launch): replace with approved quotes from real projects.
        Client names and project references should be confirmed with each client first.
      -->
      <div class="grid">
        <article v-for="item in testimonials" :key="item.client" class="card">
          <Quote class="quote-mark" />

          <blockquote>{{ item.quote }}</blockquote>

          <div class="meta">
            <strong>{{ item.name }}</strong>
            <span>{{ item.client }}</span>
            <small>{{ item.project }}</small>
          </div>
        </article>
      </div>

      <div class="bottom-actions">
        <div>
          <span>Next step</span>
          <h2>Have a site issue you want reviewed?</h2>
          <p>
            Send us the location, the works required and any photos. We will advise on a practical approach
            and the access method that suits the building.
          </p>
        </div>

        <RouterLink class="btn btn-primary" to="/contact">
          Contact Blue Speed
          <ArrowRight />
        </RouterLink>
      </div>
      </section>
    </main>

    <SiteFooter />
  </div>
</template>

<script>
import { ArrowRight, Quote } from "lucide-vue-next";

import SiteFooter from "@/components/SiteFooter.vue";
import StandardNavBar from "@/components/StandardNavBar.vue";

export default {
  name: "TestimonialPage",

  components: {
    ArrowRight,
    Quote,
    SiteFooter,
    StandardNavBar,
  },

  data() {
    return {
      testimonials: [
        {
          client: "Commercial building management team",
          project: "Façade access and crack repair",
          quote:
            "We had a tight timeline and a difficult façade position. The team showed up prepared and delivered safely with clear updates.",
          name: "Facility Operations Lead",
        },
        {
          client: "Residential estate",
          project: "Balcony inspection and maintenance",
          quote:
            "Communication was clear throughout, and the handover photos helped us close the job fast with our engineers.",
          name: "Estate Supervisor",
        },
        {
          client: "MCST council",
          project: "High-rise access and general contractor support",
          quote:
            "Everything felt organised from start to finish. We now have a preferred team for similar scope works.",
          name: "Project Coordinator",
        },
      ],
    };
  },
};
</script>

<style scoped>
.content-page {
  position: relative;
  z-index: 2;
  padding: 0 0 96px;
}

.content-shell {
  width: min(1200px, calc(100% - 40px));
  margin: 152px auto 0;
}

h1 {
  max-width: 780px;
  margin: 0;
  color: #0b2545;
  font-family: "Manrope", sans-serif;
  font-size: clamp(34px, 4.4vw, 58px);
  line-height: 1.06;
  font-weight: 800;
  letter-spacing: -0.045em;
}

h1 span {
  display: block;
  color: #7691ac;
}

.lead {
  max-width: 760px;
  margin: 24px 0 0;
  color: #5b7590;
  font-size: 15px;
  line-height: 1.8;
}

.grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 14px;
  margin-top: 48px;
}

.card {
  display: flex;
  flex-direction: column;
  padding: 30px;
  border: 1px solid #d6e3ef;
  background: #ffffff;
  box-shadow: 0 18px 50px rgba(20, 60, 105, 0.06);
  transition:
    transform 200ms ease,
    border-color 200ms ease;
}

.card:hover {
  transform: translateY(-4px);
  border-color: rgba(11, 93, 187, 0.35);
}

.quote-mark {
  width: 26px;
  height: 26px;
  color: #b6cfe8;
}

blockquote {
  flex: 1;
  margin: 22px 0 0;
  color: #0b2545;
  font-family: "Manrope", sans-serif;
  font-size: 19px;
  line-height: 1.42;
  letter-spacing: -0.02em;
}

.meta {
  display: grid;
  gap: 5px;
  margin-top: 28px;
  padding-top: 22px;
  border-top: 1px solid #e2ecf5;
}

.meta strong {
  color: #0b2545;
  font-size: 13px;
}

.meta span {
  color: #4e6d8c;
  font-size: 12px;
}

.meta small {
  color: #7691ac;
  font-size: 11px;
}

.bottom-actions {
  display: grid;
  grid-template-columns: 1fr auto;
  align-items: center;
  gap: 36px;
  margin-top: 18px;
  padding: 34px;
  border: 1px solid #d7e0ea;
  background: linear-gradient(135deg, #f4f8ff, #ffffff);
}

.bottom-actions > div > span {
  display: block;
  color: #6e7f92;
  font-size: 9px;
  font-weight: 700;
  letter-spacing: 0.14em;
  text-transform: uppercase;
}

.bottom-actions h2 {
  margin: 14px 0 0;
  color: #0b2545;
  font-family: "Manrope", sans-serif;
  font-size: 26px;
  line-height: 1.22;
  letter-spacing: -0.035em;
}

.bottom-actions p {
  max-width: 620px;
  margin: 14px 0 0;
  color: #5b7590;
  font-size: 12.5px;
  line-height: 1.75;
}

.bottom-actions .btn {
  white-space: nowrap;
}

@media (max-width: 980px) {
  .grid {
    grid-template-columns: 1fr;
  }

  .bottom-actions {
    grid-template-columns: 1fr;
  }

  .bottom-actions .btn {
    width: 100%;
  }
}

@media (max-width: 640px) {
  .content-shell {
    width: min(1200px, calc(100% - 32px));
    margin-top: 116px;
  }

  .card {
    padding: 24px;
  }

  .bottom-actions {
    padding: 26px;
  }
}
</style>
